import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenAudiencesInterface } from 'app/library/all-users/interfaces/choosen-audiences.interface';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { UNASSIGNED_AUDIENCE_FILTER_ID, UNASSIGNED_AUDIENCE_FILTER_TYPE } from 'app/library/audience/constants/unassigned-audience-filter.constant';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS } from 'app/library/audience/services/audience.api-service';
import { AudienceResource } from 'app/library/grow/models/audience-resource.model';
import { hasBrokenAudienceFilter } from 'app/library/grow/utils/audience-display.util';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-audience-filter',
    templateUrl: './audience-filter.component.html',
    styleUrls: ['./audience-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AudienceFilterComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    preSelectUnassigned: boolean;

    @Input()
    isSingleSelect: boolean;

    @Input()
    showNotAssiggnedUsersControl: boolean;

    @Input()
    alreadySelectedAudiences: IChoosenFilterInterface[] = [];

    /**
     * Grow dialogs use `growFiltersList$`; the main filter dialog uses `filtersList$` only.
     * Subscribing to both overwrote `list` with `growFiltersList$`'s initial `[]`, dropping other filters when adding an audience.
     */
    @Input()
    useGrowFiltersStream = false;

    /** Uses Engage default-setting root/search APIs; children lazy-loaded via `/children?context=defaultSetting` on expand */
    @Input()
    defaultSettingPicker = false;

    /** When set, default-setting audience lists are filtered to this experience */
    @Input()
    defaultSettingExperienceId: string | null = null;

    /** Audience pickers that must not list automatic audiences (uses isolated API fetch with `filter[excludeTypes]`). */
    @Input()
    excludeAutomaticAudiences = false;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[] | MaxBrainAudience>();

    @Output()
    choosenAudiences = new EventEmitter<IChoosenAudiencesInterface>();

    treeControl = new NestedTreeControl<IEntityNode, string>((node) => node.children ?? [], {
        trackBy: (n: IEntityNode) => n.id,
    });
    dataSource = new MatTreeNestedDataSource<IEntityNode>();
    displayedColumns = ['name', 'buttons'];

    list: IChoosenFilterInterface[] = [];
    isTableActive = true;
    searchInput = new FormControl();
    isLoading = true;
    isUnassignedSelected: boolean;
    audiences: MaxBrainAudience[] = [];
    expandedNodes: string[] = [];
    filteredData: IEntityNode[] = [];
    originalData: IEntityNode[] = [];
    loadedNodes = new Set<string>();
    selectedNode: IEntityNode;
    private _hadUnassignedChip = false;

    /** Mirrors tree expansion for template bindings (avoid `treeControl.isExpanded` in the template). */
    expandedById: Record<string, true> = {};

    hasBrokenAudienceFilter = hasBrokenAudienceFilter;

    constructor(private _audienceService: AudienceService, private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    isNodeSelectable(node: IEntityNode): boolean {
        return (node as MaxBrainAudience).selectable !== false && !hasBrokenAudienceFilter(node as MaxBrainAudience);
    }

    private _forEachTreeNode(nodes: IEntityNode[] | undefined, visit: (n: IEntityNode) => void): void {
        if (!nodes) {
            return;
        }
        for (const n of nodes) {
            visit(n);
            this._forEachTreeNode(n.children, visit);
        }
    }

    private _syncExpandedFromTree(): void {
        const next: Record<string, true> = {};
        this._forEachTreeNode(this.dataSource.data, (n) => {
            if (this.treeControl.isExpanded(n)) {
                next[n.id] = true;
            }
        });
        this.expandedById = next;
    }

    getChosenAudienceIds(): string[] {
        return this.list.filter((item) => item.entity?.type === 'Audience').map((item) => String(item.entity.id));
    }

    private async _reloadIsolatedAudiences(searchTerm: string): Promise<void> {
        this.isLoading = true;
        try {
            const audiences = await this._fetchIsolatedAudiences(searchTerm);
            this.loadedNodes.clear();
            this.audiences = audiences.map((audience) => {
                const isSelected =
                    this.alreadySelectedAudiences?.some((selected) => selected.entity.id === audience.id) ||
                    this.selectedNode?.id === audience.id ||
                    this.list.some((item) => item.entity.id === audience.id && item.entity.type === 'Audience');
                return { ...audience, isSelected };
            });
            this.updateDataSource();
        } finally {
            this.isLoading = false;
        }
    }

    private _usesIsolatedAudienceFetch(): boolean {
        return this.defaultSettingPicker || this.excludeAutomaticAudiences;
    }

    private _fetchIsolatedAudiences(searchTerm: string): Promise<MaxBrainAudience[]> {
        if (this.defaultSettingPicker) {
            return searchTerm
                ? this._audienceService.searchDefaultSettingAudiences(searchTerm, this.defaultSettingExperienceId)
                : this._audienceService.fetchDefaultSettingRootAudiences(this.defaultSettingExperienceId);
        }

        return searchTerm
            ? this._audienceService.fetchAudiencesList(searchTerm, EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS)
            : this._audienceService.fetchAudiencesList(undefined, EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS);
    }

    async ngOnInit(): Promise<void> {
        this.isUnassignedSelected = this.preSelectUnassigned;
        this.isTableActive = !this.isUnassignedSelected;

        if (this._usesIsolatedAudienceFetch()) {
            await this._reloadIsolatedAudiences(this.searchInput.value || '');
            this.searchInput.valueChanges
                .pipe(debounceTime(this.defaultSettingPicker ? 500 : 300), distinctUntilChanged(), takeUntil(this._unsubscribeAll))
                .subscribe((searchTerm) => {
                    this._reloadIsolatedAudiences((searchTerm ?? '').trim());
                });
            return;
        }

        await this._audienceService.fetchAudiences();

        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            if (!this.useGrowFiltersStream) {
                this.list = filters;
                this._syncUnassignedSelectionFromList();
                this.updateDataSource();
            }
        });

        this._allUsersService.growFiltersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            if (this.useGrowFiltersStream) {
                this.list = filters;
                this._syncUnassignedSelectionFromList();
                this.updateDataSource();
            }
        });

        if (this.showNotAssiggnedUsersControl && this.isUnassignedSelected && !this._hasUnassignedAudienceFilter()) {
            this._applyUnassignedAudienceFilter(true);
        }

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => {
            this._audienceService.fetchAudiences((searchTerm || '').trim());
            this.loadedNodes.clear();
        });

        this._audienceService.audiences$
            .pipe(
                filter((audiences) => !!audiences),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((audiences) => {
                this.audiences = audiences.map((audience) => {
                    const isSelected =
                        this.alreadySelectedAudiences?.some((selected) => selected.entity.id === audience.id) ||
                        this.selectedNode?.id === audience.id ||
                        this.list.some((item) => item.entity.id === audience.id && item.entity.type === 'Audience');

                    return { ...audience, isSelected };
                });

                this.isLoading = false;
                this.updateDataSource();

                setTimeout(() => {
                    this.filterNodes(this.searchInput.value);
                });
            });
    }

    hasChild = (_: number, node: IEntityNode): boolean => {
        if (this._usesIsolatedAudienceFetch()) {
            return (node.childrenCount ?? 0) > 0 || !!(node.children && node.children.length > 0);
        }
        return (node.childrenCount ?? 0) > 0;
    };

    updateDataSource(): void {
        this.storeExpansionState();

        if (this.audiences.length) {
            this.audiences = this.audiences.map((audience) => {
                const isSelected =
                    this.alreadySelectedAudiences?.some((selected) => selected.entity.id === audience.id) ||
                    this.selectedNode?.id === audience.id ||
                    this.list.some((item) => item.entity.id === audience.id && item.entity.type === 'Audience');
                return { ...audience, isSelected } as MaxBrainAudience;
            });

            const clonedAudiences = JSON.parse(JSON.stringify(this.audiences));
            const audienceListTree = this._audienceService.convertListToTree(clonedAudiences);

            this.dataSource.data = audienceListTree;
            this.originalData = this.dataSource.data;

            this.restoreExpansionState();
            this._syncExpandedFromTree();
        } else {
            this.dataSource.data = [];
            this.originalData = [];
            this.filteredData = [];
            this.expandedById = {};
        }
    }

    addToList(audience: AudienceResource): void {
        if (!this.isNodeSelectable(audience)) {
            return;
        }

        if (this.isUnassignedSelected || this._hasUnassignedAudienceFilter()) {
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.list = this.list.filter((item) => item.entity.type !== UNASSIGNED_AUDIENCE_FILTER_TYPE);
        }

        this.onSelect(audience);

        const maxBrainAudience = this.convertToMaxBrainAudience(audience);
        const newList: IChoosenFilterInterface[] = [
            ...this.list.filter((item) => item.entity.type !== UNASSIGNED_AUDIENCE_FILTER_TYPE),
            {
                entity: { id: maxBrainAudience.id, type: 'Audience' },
                valueToShow: `${this._translateService.instant('ENGAGE.AUDIENCE.TITLE.SINGULAR')}: ${this._translateService.instant(maxBrainAudience.name)}`,
            },
        ];

        if (this.defaultSettingPicker && !this.isSingleSelect) {
            this.list = newList;
        }

        this.addedToList.emit(newList);

        if (this.isSingleSelect) {
            this.addedToList.emit(maxBrainAudience);
        }
    }

    removeFromList(audience: AudienceResource): void {
        this.onDeselect(audience);

        // Remove from already selected audiences
        this.alreadySelectedAudiences = this.alreadySelectedAudiences.filter((item) => item.entity.id !== audience.id);

        // Remove from list
        this.list = this.list.filter((item) => item.entity.id !== audience.id);

        // Update audiences to reflect deselection
        const index = this.audiences.findIndex((a) => a.id === audience.id);
        if (index > -1) {
            this.audiences[index].isSelected = false;
        }

        // Update tree data source to reflect deselection
        this.updateDataSource();

        // Emit the updated list
        if (!this.isSingleSelect) {
            this.addedToList.emit(this.list);
        }
    }

    /**
     * Removes a selected audience by entity id regardless of whether the corresponding tree node is currently
     * loaded (e.g. when a chip in the parent dialog represents a pre-existing default whose subtree is collapsed).
     * Falls back to filtering the internal lists and re-emitting state so the parent stays in sync.
     */
    removeByEntityId(entityId: string): void {
        const matchingAudience = this.audiences.find((a) => a.id === entityId);
        if (matchingAudience) {
            this.removeFromList(matchingAudience as unknown as AudienceResource);
            return;
        }

        this.alreadySelectedAudiences = this.alreadySelectedAudiences.filter((item) => item.entity.id !== entityId);
        this.list = this.list.filter((item) => item.entity.id !== entityId);

        if (!this.isSingleSelect) {
            this.addedToList.emit(this.list);
        }
    }

    onSelect(node: IEntityNode): void {
        node.isSelected = true;
        this.selectedNode = node;
    }

    onDeselect(node: IEntityNode): void {
        node.isSelected = false;
        this.selectedNode = null;

        this._allUsersService.selectAudience(null, true);
    }

    onSelection(): void {
        this.isTableActive = !this.isUnassignedSelected;

        if (this.isUnassignedSelected) {
            this._applyUnassignedAudienceFilter(true);
            this.choosenAudiences.emit({ audiences: [], isUnassignedSelected: true });
            return;
        }

        this._applyUnassignedAudienceFilter(false);
        this.choosenAudiences.emit({
            audiences: this.dataSource.data.filter((audience) => audience.isSelected),
            isUnassignedSelected: false,
        });
    }

    private _hasUnassignedAudienceFilter(): boolean {
        return this.list.some((item) => item.entity.type === UNASSIGNED_AUDIENCE_FILTER_TYPE);
    }

    private _syncUnassignedSelectionFromList(): void {
        if (!this.showNotAssiggnedUsersControl) {
            return;
        }

        const hasUnassignedChip = this._hasUnassignedAudienceFilter();

        if (hasUnassignedChip) {
            this._hadUnassignedChip = true;
            this.isTableActive = false;

            if (!this.isUnassignedSelected) {
                this.isUnassignedSelected = true;
                this.choosenAudiences.emit({ audiences: [], isUnassignedSelected: true });
            }
            return;
        }

        if (this._hadUnassignedChip && this.isUnassignedSelected) {
            this._hadUnassignedChip = false;
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.choosenAudiences.emit({
                audiences: this.dataSource.data.filter((audience) => audience.isSelected),
                isUnassignedSelected: false,
            });
            return;
        }

        this._hadUnassignedChip = false;
        this.isTableActive = !this.isUnassignedSelected;
    }

    private _applyUnassignedAudienceFilter(isSelected: boolean): void {
        if (!this.showNotAssiggnedUsersControl) {
            return;
        }

        if (isSelected) {
            this.selectedNode = null;
            const nonAudienceFilters = this.list.filter(
                (item) => item.entity.type !== 'Audience' && item.entity.type !== UNASSIGNED_AUDIENCE_FILTER_TYPE
            );

            const updatedList: IChoosenFilterInterface[] = [
                ...nonAudienceFilters,
                {
                    entity: { id: UNASSIGNED_AUDIENCE_FILTER_ID, type: UNASSIGNED_AUDIENCE_FILTER_TYPE },
                    valueToShow: `${this._translateService.instant('ENGAGE.AUDIENCE.TITLE.SINGULAR')}: ${this._translateService.instant(
                        'GENERAL.LABEL.UNASSIGNED_AUDIENCE'
                    )}`,
                },
            ];

            this.list = updatedList;
            this.updateDataSource();
            this.addedToList.emit(updatedList);
            return;
        }

        if (!this._hasUnassignedAudienceFilter()) {
            return;
        }

        const updatedList = this.list.filter((item) => item.entity.type !== UNASSIGNED_AUDIENCE_FILTER_TYPE);
        this.list = updatedList;
        this.addedToList.emit(updatedList);
    }

    convertToMaxBrainAudience(audienceResource: AudienceResource): MaxBrainAudience {
        const maxBrainAudience = new MaxBrainAudience();
        maxBrainAudience.id = audienceResource.id;
        maxBrainAudience.name = audienceResource.name;
        maxBrainAudience.isAutoGenerated = audienceResource.isAutoGenerated;
        maxBrainAudience.isSelected = audienceResource.isSelected;

        return maxBrainAudience;
    }

    storeExpansionState(): void {
        this.expandedNodes = [];
        this.dataSource.data.forEach((node) => {
            this.treeControl.getDescendants(node).forEach((descendant) => {
                if (this.treeControl.isExpanded(descendant)) {
                    this.expandedNodes.push(descendant.id);
                }
            });
        });
    }

    restoreExpansionState(): void {
        this.dataSource.data.forEach((node) => {
            this.treeControl.expandDescendants(node);

            this.treeControl.getDescendants(node).forEach((descendant) => {
                const isExpanded = this.expandedNodes.includes(descendant.id);
                if (isExpanded) {
                    this.treeControl.expand(descendant);
                } else {
                    this.treeControl.collapse(descendant);
                }
            });
        });
    }

    /**
     * Runs after `matTreeNodeToggle` (same click). Expand/collapse is handled by the tree control;
     * this triggers lazy `/children` load when the node is expanded.
     */
    onNodeToggle(node: IEntityNode): void {
        setTimeout(() => {
            if (this.treeControl.isExpanded(node)) {
                this.loadChildren(node);
            }
        });
    }

    private _mergeAudienceChildren(children: MaxBrainAudience[]): void {
        const existing = new Set(this.audiences.map((a) => a.id));
        this.audiences.push(...children.filter((c) => !existing.has(c.id)));
    }

    async loadChildren(node: IEntityNode): Promise<void> {
        if (this.loadedNodes.has(node.id)) {
            return;
        }

        if (this._usesIsolatedAudienceFetch()) {
            if (!(node.childrenCount > 0)) {
                return;
            }

            const children = this.defaultSettingPicker
                ? await this._audienceService.fetchDefaultSettingAudienceChildren(node.id, this.defaultSettingExperienceId)
                : await this._audienceService.fetchAudienceChildrenList(node.id, EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS);
            this._mergeAudienceChildren(children);
            this.loadedNodes.add(node.id);
            this.updateDataSource();
            return;
        }

        if (!node.parent) {
            return;
        }

        const children = await this._audienceService.fetchAudienceChildren(node.id);
        this._mergeAudienceChildren(children);

        this.loadedNodes.add(node.id);
        this.updateDataSource();
    }

    filterNodes(value: string): void {
        if (value) {
            const lowerCaseValue = value.toLowerCase();
            this.filteredData = this.filterTreeNodes(this.originalData, lowerCaseValue);
        } else {
            this.filteredData = [...this.originalData];
        }

        this.dataSource.data = this.filteredData;
        this.treeControl.dataNodes = this.filteredData;

        // Check if the tree needs to be expanded
        if (value === '') {
            this.treeControl.collapseAll();
            this.loadedNodes.clear();
            if (this.filteredData[0]) {
                this.treeControl.expand(this.filteredData[0]);
            }
        } else {
            this.expandNonLeafNodes(this.filteredData);
        }

        this._syncExpandedFromTree();
    }

    expandNonLeafNodes(nodes: IEntityNode[]): void {
        nodes.forEach((node) => {
            if (node.children && node.children.length > 0) {
                this.treeControl.expand(node);
                this.expandNonLeafNodes(node.children);
            }
        });
    }

    filterTreeNodes(nodes: IEntityNode[], lowerCaseValue: string): IEntityNode[] {
        const filteredNodes: IEntityNode[] = [];

        nodes.forEach((node) => {
            const newNode: IEntityNode = { ...node, children: [] };

            if (node.children && node.children.length > 0) {
                const filteredChildren = this.filterTreeNodes(node.children, lowerCaseValue);
                if (filteredChildren.length > 0) {
                    newNode.children = filteredChildren;
                    filteredNodes.push(newNode);
                } else if (node.name.toLowerCase().includes(lowerCaseValue)) {
                    filteredNodes.push(newNode);
                }
            } else if (node.name.toLowerCase().includes(lowerCaseValue)) {
                filteredNodes.push(newNode);
            }
        });

        return filteredNodes;
    }
}
